// addAppBalance.js — the workspace's credits where a builder looks
// (wt/presets qa/briefs/WS-build-ui-r1.md finding 5).
//
// aiko found the balance only under workspace menu → Admin → the credit page;
// elena took five clicks. The builder's composer carries one line now: the
// credits this workspace holds, and — for an admin — the credit page by its own
// name (pages/add-app.js ComposerWrap.BuildBalance).
//
// The figure is the credit guard's (functions/creditGuard.js
// creditGuardState — `sdk.getCreditBalance`, loaded at boot and on every
// workspace switch): nothing is fetched here. UNKNOWN IS NOT ZERO — a balance
// that could not be read shows no line at all, never a confident "0".
//
// FRANK: self-contained (FA204); siblings through `this.call`.

// The compact balance → { text, count, tooltip, fraction, admin } or null.
//   text   "{count} credits available" in the session language;
//   admin  the viewer may open the credit page (currentUserIsAdmin).
export function addAppBalanceLine(root) {
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  if (!el || typeof el.call !== 'function' || !r || !r.signedIn) return null
  const guard = el.call('creditGuardState', r)
  if (!guard || !guard.loaded || guard.error || guard.status === 'unknown') return null
  if (guard.total == null || !Number.isFinite(Number(guard.total))) return null
  const count = Number(guard.total).toLocaleString('en-US', { maximumFractionDigits: 2 })
  const text = String(el.call('polyglot', 'workspace.addApp.balance.available')).replace(
    '{count}',
    () => count
  )
  // The number includes purchased credits. The pie specifically represents
  // the monthly allowance; a top-up must never invent a larger grant.
  // Missing allowance fields are unknown, even though the guard defaults them
  // to zero for its separate threshold policy.
  const data = r._creditGuard?.data || r._creditGuard || {}
  const known = (value) => value != null && value !== '' && Number.isFinite(Number(value))
  const hasGrant = known(data.grantSize) && Number(data.grantSize) > 0 && known(data.grantRemaining)
  const fraction = hasGrant
    ? Math.max(0, Math.min(1, Number(data.grantRemaining) / Number(data.grantSize)))
    : null
  const format = (value) => Number(value).toLocaleString('en-US', { maximumFractionDigits: 2 })
  const lines = [text]
  if (hasGrant) {
    lines.push(
      String(el.call('polyglot', 'workspace.addApp.balance.allowance'))
        .replace('{remaining}', () => format(data.grantRemaining))
        .replace('{total}', () => format(data.grantSize))
    )
  }
  if (known(data.topupRemaining) && Number(data.topupRemaining) > 0) {
    lines.push(
      String(el.call('polyglot', 'workspace.addApp.balance.purchased')).replace('{count}', () =>
        format(data.topupRemaining)
      )
    )
  }
  const admin = !!el.call('currentUserIsAdmin', r)
  if (admin) lines.push(String(el.call('polyglot', 'admin.usage.title')))
  return { text, count, tooltip: lines.join('\n'), fraction, admin }
}
